import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

/**
 * EUDatePicker — v3.8.3.
 *
 * A strict DD/MM/YYYY custom date picker built for the public booking flow.
 * The native `<input type="date">` renders in the browser locale (M/D/Y in
 * en-US) which contradicts the spec, so we ship our own popup calendar.
 *
 * Props
 * -----
 *  value:      ISO string (YYYY-MM-DD) currently selected, or "".
 *  onChange:   (iso) => void
 *  minIso:     Earliest selectable ISO date. Everything before is disabled.
 *  isDisabled: (isoDate) => bool. Extra per-day gate (lead time, min rental).
 *  placeholder Optional trigger placeholder.
 *  testId:     data-testid stem — the input gets it, the popup adds `-popup`.
 */
export default function EUDatePicker({
  value,
  onChange,
  minIso,
  isDisabled,
  placeholder = "DD/MM/YYYY",
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => monthOf(value || minIso || todayIso()));
  const wrapRef = useRef(null);

  useEffect(() => {
    if (value) setView(monthOf(value));
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, [open]);

  const displayed = value ? isoToEU(value) : "";
  const disabledCheck = (iso) => {
    if (minIso && iso < minIso) return true;
    if (isDisabled && isDisabled(iso)) return true;
    return false;
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="pb-input w-full text-left flex items-center justify-between gap-2"
        data-testid={testId}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={displayed ? "" : "text-neutral-400"}>
          {displayed || placeholder}
        </span>
        <CalendarDays size={14} className="text-neutral-400 shrink-0" />
      </button>
      {open && (
        <Popup
          view={view}
          setView={setView}
          value={value}
          disabledCheck={disabledCheck}
          onPick={(iso) => {
            onChange(iso);
            setOpen(false);
          }}
          testId={testId ? `${testId}-popup` : undefined}
        />
      )}
    </div>
  );
}

function Popup({ view, setView, value, disabledCheck, onPick, testId }) {
  const grid = useMemo(() => buildMonthGrid(view), [view]);
  const monthLabel = view.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  return (
    <div
      className="absolute z-50 mt-2 bg-white border border-black/10 rounded-lg shadow-xl p-4 w-[340px] left-0"
      role="dialog"
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => setView(addMonths(view, -1))}
          className="p-1.5 hover:bg-black/5 rounded"
          aria-label="Previous month"
          data-testid={testId ? `${testId}-prev` : undefined}
        >
          <ChevronLeft size={16} />
        </button>
        <p className="text-sm font-semibold tracking-tight">{monthLabel}</p>
        <button
          type="button"
          onClick={() => setView(addMonths(view, 1))}
          className="p-1.5 hover:bg-black/5 rounded"
          aria-label="Next month"
          data-testid={testId ? `${testId}-next` : undefined}
        >
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 mb-1 text-[10px] uppercase tracking-[0.15em] text-neutral-400">
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
          <div key={d} className="text-center py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {grid.map((cell, i) => {
          if (!cell) return <div key={i} className="h-11" />;
          const iso = cell.iso;
          const isDisabled = disabledCheck(iso);
          const isSelected = value === iso;
          return (
            <button
              key={i}
              type="button"
              disabled={isDisabled}
              onClick={() => !isDisabled && onPick(iso)}
              className={`h-11 text-sm rounded transition-colors tabular-nums ${
                isSelected
                  ? "bg-black text-white font-semibold"
                  : isDisabled
                    ? "opacity-30 pointer-events-none text-neutral-400"
                    : "hover:bg-neutral-100 text-neutral-800"
              }`}
              aria-disabled={isDisabled}
              aria-selected={isSelected}
              data-testid={testId ? `${testId}-day-${iso}` : undefined}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function isoToEU(iso) {
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}
function monthOf(iso) {
  const d = iso ? new Date(iso) : new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function pad(n) { return String(n).padStart(2, "0"); }

function buildMonthGrid(monthStart) {
  const y = monthStart.getFullYear();
  const m = monthStart.getMonth();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  // Convert JS getDay() (0=Sun) → Monday-first index (0=Mon).
  const firstJsDay = new Date(y, m, 1).getDay();
  const firstMondayIndex = (firstJsDay + 6) % 7;
  const cells = [];
  for (let i = 0; i < firstMondayIndex; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({ day, iso: `${y}-${pad(m + 1)}-${pad(day)}` });
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}
